import { TreeNode } from 'primeng/api';
import { BehaviorSubject, filter, map, tap } from 'rxjs';

import { TreeNodeData } from '../../tree/model/tree-node-data';
import { GenericSearchUtil } from '../utils/generic-search-util';
import { HisQueryParamUtils } from '../utils/his-query-param-utils';
import { HisWidgetUtils } from '../utils/his-widget-utils';
import { HisWidgetBaseSelect } from './his-widget-base-select';

/**
 * Tree select values always must have an id attribute, may have either a selected or a subTreeSelected attribute,
 * and may have arbitrary additional attributes.
 */
export class HisTreeSelectWidget extends HisWidgetBaseSelect<TreeNodeData[]> {
    kind = 'tree-select';

    treeName: string;

    treeSubject: BehaviorSubject<TreeNode[]>;

    // select tree loaded via REST URL given by widget.selectFromTree
    public tree: TreeNode[];

    constructor( options: any = {}, treeName: string, treeSubject: BehaviorSubject<TreeNode[]> ) {
        super( options );
        this.kind = options.kind || 'tree-select';
        this.treeName = treeName || null;
        this.treeSubject = treeSubject;
        treeSubject.subscribe( ( result: TreeNode[] ) => {
            console.log( 'Obtained data for tree', this.treeName );
            this.tree = result;
        } );
    }

    public static getAllTreeNodeIds( treeNodes: TreeNode[], isLidSearch: boolean ): number[] {
        let allTreeNodeIds: number[] = [];
        if ( treeNodes !== null && treeNodes !== undefined ) {
            for ( let treeNode of treeNodes ) {
                let idOrLid = isLidSearch ? treeNode.data.lid : treeNode.data.id;
                allTreeNodeIds.push( idOrLid );
                allTreeNodeIds = allTreeNodeIds.concat( this.getAllTreeNodeIds( treeNode.children, isLidSearch ) );
            }
        }
        return allTreeNodeIds;
    }

    protected doPresetInputFields( presetValue: any, onPresetSuccess: () => void ) {
        console.log( 'his-widget-tree-select#presetInputFields() #id', this.id, '#to preset', presetValue );

        this.value = [];
        this.originalValue = [];

        if ( !presetValue ) {
            return;
        }

        const isLidSearch = GenericSearchUtil.isLidPath( this.id );
        this.getWidgetValues( presetValue, isLidSearch ).subscribe( ( values: TreeNodeData[] ) => {
            console.log( 'his-widget-tree-select#presetInputFields() #values', values );

            if ( Array.isArray( values ) && values.length > 0 ) {
                values.forEach( value => this.value.push( value ) );
                this.originalValue = [...this.value];

                onPresetSuccess();
            }
        } );
    }

    /**
     * Retrieves the actual value of a widget based on the ids passed in.
     * Informs the caller about ids that could not be resolved to a node with `isMissingNodes: boolean`.
     */
    public getWidgetValueDetail( idsStr: string, isLidSearch: boolean ) {
        // idsStr is a comma-separated list of id's; a single id may have
        // an "x" appended signaling that the whole subtree shall be selected
        const { ids, idValues } = this.parseIds( idsStr );
        console.log( 'presetting input field ' + this.id + ' =', idValues );

        return this.treeSubject.pipe(
            filter( tree => !!tree ),
            map( tree => {
                const unresolvedIds: string[] = [];

                const value = ids.map( ( { id, isSubtreeSelected } ) => {
                    console.log( 'his-widget-tree-select#getWidgetValues() #id', id );
                    const foundNode = tree
                        .map( root => this.findTreeNodeWithId( root, +id, isLidSearch ) )
                        .find( node => !!node );

                    if ( !foundNode ) {
                        unresolvedIds.push( id );
                        return null;
                    }

                    // FIXME maybe use a copy instead? kept as reference to match previous implementation
                    const data: TreeNodeData = foundNode.data;
                    // XOR: either "selected" or "subtreeSelected"
                    data.selected = !isSubtreeSelected;
                    data.subTreeSelected = isSubtreeSelected;
                    return data;
                } ).filter( ( d ): d is TreeNodeData => !!d ); //filter falsy values and redefine result type

                return { value, unresolvedIds, hasUnresolvedId: unresolvedIds.length > 0 };
            } ),
            tap( ( { unresolvedIds, hasUnresolvedId } ) => {
                if ( hasUnresolvedId ) {
                    console.log( 'his-widget-tree-select#getWidgetValues() nodes not found for ids:', unresolvedIds );
                }
            } )
        );
    }

    private parseIds( idString: string ) {
        const ids = HisWidgetUtils.parseArrayString( idString ).map( id => ( {
            id: id.endsWith( 'x' ) ? id.slice( 0, -1 ) : id,
            isSubtreeSelected: id.endsWith( 'x' )
        } ) );
        const idValues = ids.map( ( { id } ) => id );
        return { ids, idValues };
    }

    private findTreeNodeWithId( treeNode: TreeNode, id: number, isLidSearch: boolean ) {
        if ( isLidSearch && treeNode.data.lid === id ) {
            return treeNode;
        }
        if ( treeNode.data.id === id ) {
            return treeNode;
        }
        let child: TreeNode;
        for ( child of treeNode.children ) {
            let foundNode: TreeNode = this.findTreeNodeWithId( child, id, isLidSearch );
            if ( foundNode ) {
                return foundNode;
            }
        }
        return null;
    }
}
